import { useEffect, useRef, useState } from 'react'
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ScrollView,
  Button,
  StyleSheet
} from 'react-native'
import { Formik } from 'formik'
import * as yup from 'yup'
import MaskInput, { Masks } from 'react-native-mask-input'
import { Feather } from '@expo/vector-icons'

// styles
import { globalStyles, colorPalette } from '../../../static/styles/global'

// i18n
import { en as formSelectEn } from '../../../i18n/FormSelect'
import { en as personalInfoEn } from '../../../i18n/PersonalInformationForms'

// components
import CustomButton from '../../shared/button/Button'
import Select from '../../shared/form/Select'
import Card from '../../shared/card/Card'
import CardSeparator from '../../shared/card/CardSeparator'
import {
  customMasks,
  deserializeDOB,
  getUnmaskedValue,
  mapSelectKey,
  setMaskedValue,
  validateEmail,
  validatePhone
} from '../../../utils'
import ScreenStatus from '../ScreenStatus'
import SelectKeyValue from '../../shared/form/SelectKeyValue'





// schema
const contactInformationSchema = (value) => yup.object({
  mainPhoneNumber: yup
    .string()
    .nullable()
    .required(personalInfoEn.contactInformation.validateMsg.phoneNumber)
    .test(
      'is-phone',
      personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
      (val) => {
        return validatePhone(val)
      }
    ),
  secondaryPhoneNumber: yup
    .string()
    .nullable()
    .test(
      'is-phone',
      personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
      (val) => {
        return !val || validatePhone(val)
      }
    ),
  employeeEmailAddress: yup.string().nullable(),

  emergencyContactName: value ? yup.string().required(personalInfoEn.contactInformation.labelMsg.contactName) : yup.string().nullable(),
  emergencyPhoneNumber: value ? yup
    .string()
    .nullable()
    .required(personalInfoEn.contactInformation.validateMsg.phoneNumber)
    .test(
      'is-phone',
      personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
      (val) => {
        return !val || validatePhone(val)
      }
    ) : yup
      .string()
      .nullable()

      .test(
        'is-phone',
        personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
        (val) => {
          return !val || validatePhone(val)
        }
      ),
  emergencyRelationshipValue: value ? yup.string().required(personalInfoEn.contactInformation.labelMsg.contactRelationship) : yup.string().nullable(),
  alternateEmergencyContactName: yup.string().nullable(),
  alternateEmergencyPhoneNumber: yup
    .string()
    .nullable()
    .test(
      'is-phone',
      personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
      (val) => {
        return !val || validatePhone(val)
      }
    ),
  alternateEmergencyRelationshipValue: yup.string().nullable()
})

// stores
import { usePersonalStore } from '../../../stores/personalInfo'

// types
import {
  PersonalStatuses,
  PersonalStatusesLocked,
  PersonalData
} from '../../../types/personalTypes'
import HomeDetailsHeader from '../HomeDetailsHeader'
import PersonalContactInfoSkeleton from '../../../skeletons/PersonalContactInfoSkeleton'
//translation
import { useTranslation } from 'react-i18next'

// initial data
type InitialValues = Pick<
  PersonalData,
  | 'mainPhoneNumber'
  | 'secondaryPhoneNumber'
  | 'employeeEmailAddress'
  | 'emergencyContactName'
  | 'emergencyPhoneNumber'
  | 'emergencyRelationshipValue'
  | 'alternateEmergencyContactName'
  | 'alternateEmergencyPhoneNumber'
  | 'alternateEmergencyRelationshipValue'

>

const initialValues: InitialValues = {
  mainPhoneNumber: '',
  secondaryPhoneNumber: '',
  employeeEmailAddress: 'judith@gmail.com',
  emergencyContactName: '',
  emergencyPhoneNumber: '',
  emergencyRelationshipValue: '',
  alternateEmergencyContactName: '',
  alternateEmergencyPhoneNumber: '',
  alternateEmergencyRelationshipValue: '',

}

const ContactInformationForm = ({ navigation, route }: any): React.ReactNode => {
  const { t } = useTranslation()
  const formikRef = useRef(null)
  const [status, setStatus] = useState<PersonalStatuses | null>(null)

  // params
  const { params } = route
  const { mostRecentUserOnBoardings: userId, relationships } = params

  // stores' data
  const { loading, error, personalData, fetchData, updateData } =
    usePersonalStore()

  const [alternativeEmergencyContact, setAlternativeEmergencyContact] =
    useState<boolean>(false)

  const handleAddSecondaryEmergencyContact = () => {
    setAlternativeEmergencyContact(true)
  }

  const handleRemoveSecondaryEmergencyContact = (props: any) => {
    props.setFieldValue('alternateEmergencyContactName', '')
    props.setFieldValue('alternateEmergencyPhoneNumber', '')
    props.setFieldValue('alternateEmergencyRelationshipValue', '')
    setAlternativeEmergencyContact(false)
  }

  useEffect(() => {
    fetchData(userId)
  }, [])

  useEffect(() => {
    if (formikRef.current) {
      setMaskedValue('mainPhoneNumber', Masks.USA_PHONE, formikRef)
      setMaskedValue('secondaryPhoneNumber', Masks.USA_PHONE, formikRef)
      setMaskedValue('emergencyPhoneNumber', Masks.USA_PHONE, formikRef)
      setMaskedValue(
        'alternateEmergencyPhoneNumber',
        Masks.USA_PHONE,
        formikRef
      )

      if (
        formikRef.current.values.alternateEmergencyContactName ||
        formikRef.current.values.alternateEmergencyPhoneNumber ||
        formikRef.current.values.alternateEmergencyRelationshipValue
      ) {
        setAlternativeEmergencyContact(true)
      }
    }

    if (personalData?.statusDescription)
      setStatus(personalData.statusDescription)

    return () => { }
  }, [formikRef.current, loading])

  if (loading) return <PersonalContactInfoSkeleton />

  if (error) return <ScreenStatus message={error} />





  return (
    personalData &&
    status && (
      < >
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader
              title={t('personalinformation')}
              description={t('personaldescription')}
            />
          </View>
        </View>
        <View style={[globalStyles.containerOverlap]}>
          <Formik
            enableReinitialize
            innerRef={formikRef}
            initialValues={{
              ...initialValues,
              ...personalData
            }}
            validationSchema={contactInformationSchema(personalData?.isEmergencyContactMandatory)}
            onSubmit={(values, actions) => {
              actions.resetForm()

              // TODO: call action to save values to DB - remove white spaces from values entries on frontend
              const contactInfoDto = {
                ...values,
                mainPhoneNumber: getUnmaskedValue(
                  values.mainPhoneNumber,
                  Masks.USA_PHONE
                ),
                secondaryPhoneNumber: getUnmaskedValue(
                  values.secondaryPhoneNumber,
                  Masks.USA_PHONE
                ),
                emergencyPhoneNumber: getUnmaskedValue(
                  values.emergencyPhoneNumber,
                  Masks.USA_PHONE
                ),
                alternateEmergencyPhoneNumber: getUnmaskedValue(
                  values.alternateEmergencyPhoneNumber,
                  Masks.USA_PHONE
                )
              }
              // console.log('contactInfoDto:', contactInfoDto)

              // status will be PENDING
              const submitted = { Submitted: true }

              let {
                essUserOnBoardingKey,
                employeeEmailAddress,
                ssnLastFour,
                statusDescription,
                ...personalDto
              } = contactInfoDto

              personalDto = { ...personalDto, ...submitted }

              // console.log('personalDto:', personalDto)

              // update the database when the status neither PENDING nor COMPLETED
              if (!PersonalStatusesLocked.includes(status)) {
                updateData(userId, personalDto)

                // go back to homepage

                if (personalData?.hasCanary) {
                  navigation.navigate('CanarayForm')
                }
                else {
                  navigation.navigate('Onboarding')
                }

              } else {
                // go prev to basic information form
                navigation.replace('PersonalBasicInformation')
              }
            }}
          >
            {(props) => (
              <View>
                <Card>
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.vCenterItems,
                      globalStyles.hBetweenItems,
                      globalStyles.flexWrapped
                    ]}
                  >
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fwBold,
                        globalStyles.fs18,
                        globalStyles.blackTextColor
                      ]}
                    >
                      {t('contactinformation')}
                    </Text>
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fs14,
                        globalStyles.grayTextColor,
                        globalStyles.pt2
                      ]}
                    >
                      <Text style={styles.required}>*{t('requiredfield')}</Text>
                    </Text>
                  </View>
                  <CardSeparator light={true} />
                  <View>
                    {/* <Text>{JSON.stringify(props, null, 2)}</Text> */}
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('mainphonenumber')}<Text style={styles.required}>*</Text>
                      </Text>
                      <MaskInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.mainPhoneNumber &&
                          props.errors.mainPhoneNumber &&
                          globalStyles.inputError
                        ]}
                        value={props.values.mainPhoneNumber || ''}
                        onChangeText={props.handleChange('mainPhoneNumber')}
                        onBlur={props.handleBlur('mainPhoneNumber')}
                        mask={Masks.USA_PHONE}
                        keyboardType="phone-pad"
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.mainPhoneNumber &&
                        props.errors.mainPhoneNumber && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.mainPhoneNumber}
                          </Text>
                        )}
                    </View>
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('secondaryphonenumber')}
                      </Text>
                      <MaskInput
                        style={[
                          globalStyles.input,
                          globalStyles.inputLight,
                          PersonalStatusesLocked.includes(status) &&
                          globalStyles.inputReadOnly,
                          props.touched.secondaryPhoneNumber &&
                          props.errors.secondaryPhoneNumber &&
                          globalStyles.inputError
                        ]}
                        value={props.values.secondaryPhoneNumber || ''}
                        onChangeText={props.handleChange(
                          'secondaryPhoneNumber'
                        )}
                        onBlur={props.handleBlur('secondaryPhoneNumber')}
                        mask={Masks.USA_PHONE}
                        keyboardType="phone-pad"
                        editable={!PersonalStatusesLocked.includes(status)}
                      />
                      {props.touched.secondaryPhoneNumber &&
                        props.errors.secondaryPhoneNumber && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.secondaryPhoneNumber}
                          </Text>
                        )}
                    </View>
                    <View style={[globalStyles.mb2]}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('personalemailaddress')}<Text style={styles.required}>*</Text>
                      </Text>
                      <View style={[globalStyles.inputWithIconWrapper]}>
                        <TextInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            globalStyles.inputReadOnly,
                            props.touched.employeeEmailAddress &&
                            props.errors.employeeEmailAddress &&
                            globalStyles.inputError
                          ]}
                          placeholder={'example@email.com'}
                          onChangeText={props.handleChange(
                            'employeeEmailAddress'
                          )}
                          value={props.values.employeeEmailAddress || ''}
                          onBlur={props.handleBlur('employeeEmailAddress')}
                          editable={false}
                        />
                        {/* <TouchableOpacity
                    onPress={() => {}}
                    style={[
                      globalStyles.inputWithIcon,
                      globalStyles.inputWithIconEnd,
                      { bottom: 13 }
                    ]}
                  >
                    <Feather
                      name="edit"
                      size={22}
                      color={colorPalette.secondary}
                    />
                  </TouchableOpacity> */}
                      </View>
                      {props.touched.employeeEmailAddress &&
                        props.errors.employeeEmailAddress && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.employeeEmailAddress}
                          </Text>
                        )}
                    </View>

                    <View>
                      <View style={[globalStyles.mb2]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >

                          {t('emergencycontact')}{personalData?.isEmergencyContactMandatory && <Text style={styles.required}>*</Text>}
                        </Text>
                        <TextInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            props.touched.emergencyContactName &&
                            props.errors.emergencyContactName &&
                            globalStyles.inputError,
                            { textTransform: 'uppercase' }
                          ]}
                          placeholder={''}
                          onChangeText={props.handleChange(
                            'emergencyContactName'
                          )}
                          value={props.values.emergencyContactName || ''}
                          onBlur={props.handleBlur('emergencyContactName')}
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.emergencyContactName &&
                          props.errors.emergencyContactName && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.emergencyContactName}
                            </Text>
                          )}
                      </View>
                      <View style={[globalStyles.mb2]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('emergencyphonenumber')}{personalData?.isEmergencyContactMandatory && <Text style={styles.required}>*</Text>}
                        </Text>
                        <MaskInput
                          style={[
                            globalStyles.input,
                            globalStyles.inputLight,
                            PersonalStatusesLocked.includes(status) &&
                            globalStyles.inputReadOnly,
                            props.touched.emergencyPhoneNumber &&
                            props.errors.emergencyPhoneNumber &&
                            globalStyles.inputError
                          ]}
                          onChangeText={props.handleChange(
                            'emergencyPhoneNumber'
                          )}
                          value={props.values.emergencyPhoneNumber || ''}
                          onBlur={props.handleBlur('emergencyPhoneNumber')}
                          mask={Masks.USA_PHONE}
                          keyboardType="phone-pad"
                          editable={!PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.emergencyPhoneNumber &&
                          props.errors.emergencyPhoneNumber && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.emergencyPhoneNumber}
                            </Text>
                          )}
                      </View>

                      <View style={[globalStyles.mb2]}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary,
                            props.touched.emergencyRelationshipValue &&
                            props.errors.emergencyRelationshipValue &&
                            globalStyles.inputError
                          ]}
                        >
                          {t('emergencyrelation')}{personalData?.isEmergencyContactMandatory && <Text style={styles.required}>*</Text>}
                        </Text>
                        <SelectKeyValue
                          title={t('Contact Relationship')}
                          options={relationships}
                          selectedOption={mapSelectKey(
                            relationships,
                            personalData.emergencyRelationshipValue
                          )}
                          onSelect={(option: string) => {
                            props.setFieldValue(
                              'emergencyRelationshipValue',
                              option
                            )
                          }}
                          isDisabled={PersonalStatusesLocked.includes(status)}
                        />
                        {props.touched.emergencyRelationshipValue &&
                          props.errors.emergencyRelationshipValue && (
                            <Text style={globalStyles.dangerAlert}>
                              {props.errors.emergencyRelationshipValue}
                            </Text>
                          )}
                      </View>
                    </View>
                    {/* emergency contact */}
                    {alternativeEmergencyContact && (
                      <View>
                        <View
                          style={{
                            borderBottomColor: '#EEEEEE',
                            borderBottomWidth: 1,
                            marginTop: 15,
                            marginBottom: 20
                          }}
                        />
                        <View style={[globalStyles.mb4]}>
                          <Text
                            style={[
                              globalStyles.text,
                              globalStyles.fwBold,
                              globalStyles.fs14,
                              globalStyles.primaryTextColor
                            ]}
                          >
                            {t('alternative')}
                          </Text>
                        </View>
                        {/* separator */}
                        <View style={[globalStyles.mb2]}>
                          <Text
                            style={[
                              globalStyles.inputLabel,
                              globalStyles.inputLabelLarge,
                              globalStyles.inputLabelPrimary
                            ]}
                          >
                            {t('emergencycontact')}
                          </Text>
                          <TextInput
                            style={[
                              globalStyles.input,
                              globalStyles.inputLight,
                              PersonalStatusesLocked.includes(status) &&
                              globalStyles.inputReadOnly, { textTransform: 'uppercase' }
                            ]}
                            placeholder={''}
                            onChangeText={props.handleChange(
                              'alternateEmergencyContactName'
                            )}
                            value={
                              props.values.alternateEmergencyContactName || ''
                            }
                            onBlur={props.handleBlur(
                              'alternateEmergencyContactName'
                            )}
                            editable={!PersonalStatusesLocked.includes(status)}
                          />
                        </View>
                        <View style={[globalStyles.mb2]}>
                          <Text
                            style={[
                              globalStyles.inputLabel,
                              globalStyles.inputLabelLarge,
                              globalStyles.inputLabelPrimary
                            ]}
                          >
                            {t('emergencyphonenumber')}
                          </Text>
                          <MaskInput
                            style={[
                              globalStyles.input,
                              globalStyles.inputLight,
                              PersonalStatusesLocked.includes(status) &&
                              globalStyles.inputReadOnly,
                              props.touched.alternateEmergencyPhoneNumber &&
                              props.errors.alternateEmergencyPhoneNumber &&
                              globalStyles.inputError
                            ]}
                            onChangeText={props.handleChange(
                              'alternateEmergencyPhoneNumber'
                            )}
                            value={
                              props.values.alternateEmergencyPhoneNumber || ''
                            }
                            onBlur={props.handleBlur(
                              'alternateEmergencyPhoneNumber'
                            )}
                            mask={Masks.USA_PHONE}
                            keyboardType="phone-pad"
                            editable={!PersonalStatusesLocked.includes(status)}
                          />
                          {props.touched.alternateEmergencyPhoneNumber &&
                            props.errors.alternateEmergencyPhoneNumber && (
                              <Text style={globalStyles.dangerAlert}>
                                {props.errors.alternateEmergencyPhoneNumber}
                              </Text>
                            )}
                        </View>
                        <View style={[globalStyles.mb2]}>
                          <Text
                            style={[
                              globalStyles.inputLabel,
                              globalStyles.inputLabelLarge,
                              globalStyles.inputLabelPrimary
                            ]}
                          >
                            {t('emergencyrelation')}
                          </Text>
                          <SelectKeyValue
                            title={t('Contact Relationship')}
                            options={relationships}
                            selectedOption={mapSelectKey(
                              relationships,
                              personalData.alternateEmergencyRelationshipValue
                            )}
                            onSelect={(option: string) => {
                              props.setFieldValue(
                                'alternateEmergencyRelationshipValue',
                                option
                              )
                            }}
                            isDisabled={PersonalStatusesLocked.includes(status)}
                          />
                        </View>
                      </View>
                    )}
                    {/* alternative emergency contact */}

                    <View style={[globalStyles.mt1, globalStyles.mb1]}>
                      {!alternativeEmergencyContact
                        ? !PersonalStatusesLocked.includes(status) && (
                          <TouchableOpacity
                            onPress={handleAddSecondaryEmergencyContact}
                          >
                            <Text
                              style={[
                                globalStyles.text,
                                globalStyles.fs14,
                                globalStyles.primaryTextColor,
                                globalStyles.textUnderline
                              ]}
                            >
                              +{t('addemergency')}
                            </Text>
                          </TouchableOpacity>
                        )
                        : !PersonalStatusesLocked.includes(status) && (
                          <TouchableOpacity
                            onPress={() =>
                              handleRemoveSecondaryEmergencyContact(props)
                            }
                          >
                            <Text
                              style={[
                                globalStyles.text,
                                globalStyles.fs14,
                                globalStyles.dangerTextColor,
                                globalStyles.textUnderline
                              ]}
                            >
                              x{t('remove')}
                            </Text>
                          </TouchableOpacity>
                        )}
                    </View>
                    {/* add / remove secondary emergency contact */}
                  </View>
                </Card>
                <View
                  style={{
                    marginTop: 10,
                    marginBottom: 25,
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <CustomButton
                    text={
                      PersonalStatusesLocked.includes(status)
                        ? t('previous')
                        : t('cancel')
                    }
                    color={
                      PersonalStatusesLocked.includes(status) ? 'primary' : 'warn'
                    }
                    onPress={() => {
                      if (PersonalStatusesLocked.includes(status)) {
                        navigation.navigate('PersonalBasicInformation')
                      } else {
                        navigation.navigate('Onboarding')
                      }
                    }}
                  />

                  <CustomButton
                    text={
                      PersonalStatusesLocked.includes(status)
                        ? t('home')
                        : personalData?.hasCanary ? t('next') : t('submitinfo')
                    }
                    onPress={() =>
                      PersonalStatusesLocked.includes(status)
                        ? navigation.navigate('Onboarding') // Replace 'Home' with your actual screen name
                        : props.handleSubmit()
                    }
                    color={'primary'}
                  />
                </View>
              </View>
            )}
          </Formik>
        </View>
      </ >
    )
  )
}

const styles = StyleSheet.create({
  required: {
    color: "red"
  }
})

export default ContactInformationForm
